import { Accordion } from '@liveauctioneers/hammer-ui-core/accordion';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { ViewMore } from '@liveauctioneers/hammer-ui-core/viewMore';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { colorBackgroundPrimary } from '@liveauctioneers/hammer-ui-theme/vars';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { VIEW_MORE_CUTOFF_HEIGHT } from '@/pages/Search/SearchPage/types';

type Props = {
    children: ReactNode;
    headline: string;
    /** While true, a spinner renders below the option list -- bridges the gap
     *  between the primitive's instant uncontrolled expand and the server
     *  returning the larger bucket ~1-2s later. */
    isLoading?: boolean;
    /** Fires on every toggle click. Server-paginated facets use this to dispatch the
     *  on-demand "View More" fetch the first time. Optional for facets whose full
     *  list ships in the initial response (e.g. Origin). */
    onViewMoreClick?: () => void;
};

// Shared shell for AllFilters checkbox/link facets. Wraps Accordion + ViewMore
// + scroll container so the fade-and-peek primitive and View More toggle stay
// visually consistent across Creator, Material, Style, Subject, Origin, and
// AuctionHouse's default-mode list.
//
// ViewMore is uncontrolled (its `expanded` is internal; only `defaultExpanded`
// is exposed). When the URL changes -- e.g. a filter is applied -- we remount
// the primitive via `key={search}` to reset its expand state. The local
// `isExpanded` mirror is only for the inner overflow-y-auto class toggle and
// the loading-spinner gate.
export const FacetCard = ({ children, headline, isLoading, onViewMoreClick }: Props) => {
    const { formatMessage } = useIntl();
    const { search } = useLocation();
    const [isExpanded, setIsExpanded] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setIsExpanded(false);
    }, [search]);

    // Reset scroll on collapse so the user lands back on the top of the
    // list rather than wherever they had scrolled inside the expanded view.
    const handleChange = useCallback((expanded: boolean) => {
        setIsExpanded(expanded);
        if (!expanded && scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
    }, []);

    return (
        <Accordion
            className="w-full"
            density="compact"
            emphasis="subtle"
            headline={headline}
            open
        >
            <ViewMore
                collapseText={formatMessage({ id: 'view_less' })}
                expandText={formatMessage({ id: 'view_more' })}
                fadeBackgroundColor={`rgb(var(${colorBackgroundPrimary}))`}
                key={search}
                onChange={handleChange}
                onClick={onViewMoreClick}
                textCutoffHeight={VIEW_MORE_CUTOFF_HEIGHT}
            >
                <div
                    className={cn('flex max-h-[400px] flex-col gap-xs overflow-y-hidden', {
                        'overflow-y-auto': isExpanded,
                    })}
                    ref={scrollContainerRef}
                >
                    {children}
                    {isExpanded && isLoading && (
                        <div className="flex justify-center py-xs">
                            <Loading />
                        </div>
                    )}
                </div>
            </ViewMore>
        </Accordion>
    );
};
